import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, Heart } from 'lucide-react';
import SEO from '../components/SEO';
import ChecklistProgress from '../components/checklist/ChecklistProgress';
import ChecklistPhase from '../components/checklist/ChecklistPhase';
import { useChecklist } from '../hooks/useChecklist';

export default function WeddingChecklistPage() {
  const { phases, done, total, doneCount, percent, toggle, addCustom, removeCustom, reset } = useChecklist();
  const [filter, setFilter] = useState('all');

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAF9F7' }} data-testid="checklist-page">
      <SEO
        title="Wedding Planning Checklist for Malta"
        description="The free interactive wedding checklist for couples marrying in Malta and Gozo: 60+ tasks from 12 months out to the wedding day, with progress saved in your browser."
        keywords="wedding checklist, wedding planning checklist malta, wedding to do list, wedding planning timeline, getting married in malta checklist"
        breadcrumbs={[{ name: 'Wedding Checklist', url: '/checklist' }]}
      />

      <section className="relative h-[300px] md:h-[400px] overflow-hidden">
        <img
          src="/images/articles/wedding-cost-malta-planning.jpg"
          alt="Wedding planning notebook with blush peonies"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-[#FAF9F7]" />
        <div className="absolute inset-0 flex items-center justify-center px-6 text-center">
          <div>
            <p className="text-white/90 text-xs tracking-[0.3em] uppercase mb-4">Plan your day, the Malta way</p>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight drop-shadow-lg"
              style={{ fontFamily: '"Playfair Display", serif' }}
              data-testid="checklist-heading"
            >
              Wedding Planning Checklist
            </h1>
          </div>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 -mt-16 relative z-10 pb-24">
        <ChecklistProgress
          percent={percent}
          doneCount={doneCount}
          total={total}
          filter={filter}
          onFilter={setFilter}
          onReset={reset}
        />

        {percent === 100 && (
          <div
            className="mt-6 rounded-2xl p-8 text-center text-white animate-fade-in-up"
            style={{ backgroundColor: '#DE92A9' }}
            data-testid="checklist-complete-banner"
          >
            <Heart size={36} className="mx-auto mb-3" fill="white" />
            <p className="text-2xl mb-2" style={{ fontFamily: '"Playfair Display", serif' }}>Every box ticked. Congratulations!</p>
            <p className="text-white/80 text-sm">
              We would love to feature your day.{' '}
              <Link to="/submit-wedding" className="underline font-medium" data-testid="checklist-submit-link">Submit your wedding</Link>
            </p>
          </div>
        )}

        <div className="mt-10 space-y-8" data-testid="checklist-phases">
          {phases.map((phase, i) => (
            <ChecklistPhase
              key={phase.id}
              phase={phase}
              index={i}
              done={done}
              filter={filter}
              onToggle={toggle}
              onAdd={addCustom}
              onRemove={removeCustom}
            />
          ))}
        </div>

        <div className="mt-16 text-center">
          <p className="text-sm mb-5" style={{ color: '#757B7F' }}>Want dates against each phase?</p>
          <Link
            to="/countdown"
            className="inline-flex items-center gap-2 text-xs tracking-[0.2em] font-semibold px-8 py-4 border-2 transition-all hover:gap-3 group"
            style={{ borderColor: '#DE92A9', color: '#DE92A9' }}
            data-testid="checklist-countdown-link"
          >
            <Clock size={14} />
            START YOUR COUNTDOWN
            <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </div>
  );
}
